/**
 * useRequireAuth Hook
 *
 * Provides a mechanism to require authentication before executing actions.
 * Shows login modal with custom message when user is not authenticated.
 *
 * This implements the "soft gate" pattern where users can browse freely
 * but must login to use generation features (Video, Audio, Chat, Dubbing, STT).
 *
 * NOTE: Auth requirement is BYPASSED in local development environment
 * (when VITE_ENVIRONMENT !== 'production' and VITE_ENVIRONMENT !== 'staging')
 * because localhost cannot be added to Firebase authorized domains.
 */

import { useCallback, useState, useMemo } from 'react';
import { useAuth } from './useAuth';
import { isLocalDevelopment } from '../utils/apiMode';

/**
 * Return type for the useRequireAuth hook.
 */
export interface UseRequireAuthReturn {
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether login modal is open */
  isLoginModalOpen: boolean;
  /** Custom message to show in the login modal */
  loginMessage: string | null;
  /** Open the login modal with optional custom message */
  openLoginModal: (message?: string) => void;
  /** Close the login modal */
  closeLoginModal: () => void;
  /** Execute action if authenticated, otherwise show login modal */
  requireAuth: (action: () => void | Promise<void>, message?: string) => void;
  /** Wrap a callback to require authentication */
  withAuth: <T extends (...args: unknown[]) => void | Promise<void>>(
    callback: T,
    message?: string
  ) => (...args: Parameters<T>) => void;
}

/**
 * Hook to require authentication before executing actions.
 *
 * @returns Object with authentication state and methods
 *
 * @example
 * ```tsx
 * const { requireAuth, isLoginModalOpen, closeLoginModal, loginMessage } = useRequireAuth();
 *
 * const handleGenerate = () => {
 *   requireAuth(() => {
 *     // This only runs if user is authenticated
 *     generateVideo(config);
 *   }, 'Please sign in to generate videos');
 * };
 *
 * return (
 *   <>
 *     <button onClick={handleGenerate}>Generate</button>
 *     <LoginModal
 *       isOpen={isLoginModalOpen}
 *       onClose={closeLoginModal}
 *       message={loginMessage}
 *     />
 *   </>
 * );
 * ```
 */
export const useRequireAuth = (): UseRequireAuthReturn => {
  const { user } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginMessage, setLoginMessage] = useState<string | null>(null);

  // In local development, always consider user as authenticated (bypass auth)
  const bypassAuth = useMemo(() => isLocalDevelopment(), []);
  const isAuthenticated = bypassAuth || !!user;

  /**
   * Open the login modal with optional custom message.
   */
  const openLoginModal = useCallback((message?: string) => {
    setLoginMessage(message ?? null);
    setIsLoginModalOpen(true);
  }, []);

  /**
   * Close the login modal.
   */
  const closeLoginModal = useCallback(() => {
    setIsLoginModalOpen(false);
    setLoginMessage(null);
  }, []);

  /**
   * Execute action if authenticated, otherwise show login modal.
   */
  const requireAuth = useCallback(
    (action: () => void | Promise<void>, message?: string) => {
      if (isAuthenticated) {
        action();
      } else {
        openLoginModal(message);
      }
    },
    [isAuthenticated, openLoginModal]
  );

  /**
   * Wrap a callback to require authentication.
   * Useful for event handlers.
   */
  const withAuth = useCallback(
    <T extends (...args: unknown[]) => void | Promise<void>>(callback: T, message?: string) => {
      return (...args: Parameters<T>) => {
        if (isAuthenticated) {
          callback(...args);
        } else {
          openLoginModal(message);
        }
      };
    },
    [isAuthenticated, openLoginModal]
  );

  return {
    isAuthenticated,
    isLoginModalOpen,
    loginMessage,
    openLoginModal,
    closeLoginModal,
    requireAuth,
    withAuth,
  };
};
